import React, { useEffect, useState } from 'react';
import { fetchProducts, fetchCategories, fetchBrands, adminCreateProduct, adminUpdateProduct, adminDeleteProduct } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const emptyForm = {
  name: '', brand: '', category: '', price: 0, originalPrice: '', image: '',
  inStock: true, description: '', flavors: '', featured: false,
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or 'new'
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const { toast } = useToast();

  const reload = async () => {
    setLoading(true);
    const [p, c, b] = await Promise.all([fetchProducts({ limit: 500 }), fetchCategories(), fetchBrands()]);
    setProducts(p); setCategories(c); setBrands(b);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const openNew = () => {
    setEditing('new');
    setForm({ ...emptyForm, brand: brands[0]?.id || '', category: categories[0]?.id || '' });
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, brand: p.brand, category: p.category, price: p.price,
      originalPrice: p.originalPrice ?? '', image: p.image,
      inStock: p.inStock, description: p.description || '',
      flavors: (p.flavors || []).join(', '), featured: !!p.featured,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        originalPrice: form.originalPrice === '' ? null : parseFloat(form.originalPrice),
        flavors: form.flavors.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (editing === 'new') {
        await adminCreateProduct(payload);
        toast({ title: 'Product created' });
      } else {
        await adminUpdateProduct(editing.id, payload);
        toast({ title: 'Product updated' });
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast({ title: 'Deleted' });
      reload();
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const filtered = products.filter(p => p.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black">Products</h1>
          <p className="text-sm text-neutral-500">{products.length} total — click to edit.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0a0a0a] hover:bg-neutral-800"><Plus className="w-4 h-4 mr-1" /> New Product</Button>
      </div>

      <Input placeholder="Search products..." value={filter} onChange={e => setFilter(e.target.value)} className="mb-4 max-w-sm bg-white" />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-neutral-400" /></div>
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-t hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={p.image} alt="" className="w-10 h-10 object-contain bg-neutral-50 rounded shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold truncate max-w-md">{p.name}</div>
                        <div className="text-xs text-neutral-500 truncate">{p.category}{p.featured && ' • Featured'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize">{p.brand}</td>
                  <td className="px-4 py-3 font-bold">${p.price.toFixed(2)}</td>
                  <td className="px-4 py-3">{p.inStock ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">In Stock</Badge> : <Badge variant="secondary">Out</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(p)} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-neutral-500">No products match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'New Product' : 'Edit Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Brand *</Label>
                <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price *</Label><Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>Original price (for sale)</Label><Input type="number" step="0.01" value={form.originalPrice} onChange={e => setForm({ ...form, originalPrice: e.target.value })} placeholder="Optional" /></div>
            </div>
            <div><Label>Image URL *</Label><Input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
              {form.image && <img src={form.image} alt="" className="mt-2 w-24 h-24 object-contain bg-neutral-50 rounded border" />}
            </div>
            <div><Label>Flavors (comma-separated)</Label><Input value={form.flavors} onChange={e => setForm({ ...form, flavors: e.target.value })} placeholder="Vanilla, Chocolate, Strawberry" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.inStock} onCheckedChange={(v) => setForm({ ...form, inStock: v })} /><span className="text-sm font-semibold">In Stock</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /><span className="text-sm font-semibold">Featured</span></label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.brand || !form.category || !form.image} className="bg-[#0a0a0a]">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
